// The document schema core: identity, versioning, deterministic bytes, and
// the fields each kind must carry.
//
// This module is deliberately FREE OF THE MUSIC MODEL — types only, no value
// imports — because the dev writer's Vite plugin loads it in plain Node,
// where the app's `@/` alias does not exist. Pulling `validateScore` in here
// would drag the whole score model (normalize, duration, timing) into the dev
// server's config load and break it outright.
//
// So the split is by DEPTH, not by duplication: this file answers "is this a
// well-formed document of kind X", `documents.ts` re-exports all of it and
// adds "…and is the music inside it actually readable". App code imports
// `documents.ts` and gets the deep check; the writer endpoint imports this and
// gets the shape check, which is all an allow-listed path resolver needs.

import type { Arrangement, GrooveState } from '@/music/arrangement';
import type { Score } from '@/music/score/types';
import type { VoicingFamilySelection } from '@/music/voicingFamilies';

/** Bumped when a document's shape changes; `migrate` hooks read it. */
export const SCHEMA_VERSION = 1;

/** Opaque, stable, human-greppable. Never a display title. */
export type DocumentId = string;

/** Which store a document lives in. */
export type StoreId = 'repository' | 'browser' | 'account';

/**
 * A backend-neutral pointer to one exact version of one document. Reads
 * resolve an exact revision (so a cache can key by it); writes carry the
 * revision they expect, so a stale save is a conflict rather than a
 * clobber.
 */
export interface DocumentLocator {
  storeId: StoreId;
  id: DocumentId;
  /** Absent means "whatever is current". */
  revisionId?: string;
}

/** Who controls a document. */
export type PrincipalRef =
  | { kind: 'system'; id: string }
  | { kind: 'local-user' }
  | { kind: 'user'; id: string };

/**
 * Where a document's content came from. A discriminated union rather than a
 * free-text note, because provenance is the input to a rights decision and a
 * decision cannot be made from prose.
 */
export type DocumentProvenance =
  | { kind: 'original'; credit?: string }
  | { kind: 'editorial-transcription'; credit?: string; sourceNote?: string }
  | { kind: 'import'; format: 'musicxml' | 'openbook' | 'ireal' | 'drum-loop'; sourceId?: string };

/**
 * May this be distributed?
 *
 * `review-required` is the load-bearing value and the DEFAULT posture for
 * anything derived from someone else's work. The deploy is unlisted precisely
 * because the bundle carries content nobody has cleared; a status field a
 * build can CHECK is the mechanism that turns "we should do a content pass
 * before going public" from an intention into a gate. A free-text note cannot
 * be grepped in CI.
 */
export interface DocumentRights {
  status: 'owned' | 'licensed' | 'public-domain' | 'review-required';
  note?: string;
}

export interface DocumentMeta {
  id: DocumentId;
  schemaVersion: number;
  /** Set by the store on write; never accepted from the browser. */
  revisionId?: string;
  /** Who controls this document; absent means the local user. */
  controller?: PrincipalRef;
  /** Where the content came from. */
  provenance?: DocumentProvenance;
  /** Whether it may be distributed. */
  rights?: DocumentRights;
  /** A derivative keeps its lineage while taking a new identity. */
  derivedFrom?: { id: DocumentId; revisionId?: string };
}

const PROVENANCE_KINDS = ['original', 'editorial-transcription', 'import'] as const;
const RIGHTS_STATUSES = ['owned', 'licensed', 'public-domain', 'review-required'] as const;

/**
 * Does this document need a human rights decision before it can ship?
 *
 * Absent rights count as "yes". Silence is not clearance — a document nobody
 * has assessed is exactly the one a public build must stop on.
 */
export function needsRightsReview(doc: { rights?: DocumentRights }): boolean {
  return !doc.rights || doc.rights.status === 'review-required';
}

/**
 * A reusable groove template. Immutable once published: applying it copies
 * `state` into a tune, and a later edit creates a NEW revision rather than
 * mutating this one.
 */
export interface GroovePresetDoc extends DocumentMeta {
  kind: 'groove';
  name: string;
  /** Free-text grouping for the picker ("Swing", "Latin", "Funk"). */
  category?: string;
  state: GrooveState;
}

/**
 * A snapshot of a groove copied into a tune. It records where it came from
 * for display ("based on Afro-Cuban Night"), but the copied `state` is
 * authoritative — the source may change or vanish without effect.
 */
export interface GrooveSnapshot {
  state: GrooveState;
  from?: { presetId: DocumentId; revisionId?: string; name?: string };
}

/** Everything needed to reproduce a generated voicing line. Engine ids stay
 * strings at the storage boundary so a document written by a future build is
 * readable; the registry resolves unavailable ids safely at use time. */
export interface VoiceLeadingArrangementSettings {
  engineId: string;
  options: {
    voicingFamily: VoicingFamilySelection;
    stringSet: 'middle' | 'upper';
    freeStringSet: boolean;
    avoidB9: boolean;
    startingInversion: number;
  };
}

/**
 * A tune's arrangement: which groove each semantic Feel resolves to, plus
 * per-occurrence overrides. Feels are named by the score's own vocabulary
 * ("Latin", "Swing"), so two tunes can map the same printed word differently.
 */
export interface TuneArrangementDoc extends DocumentMeta {
  kind: 'arrangement';
  /** The score this arranges. */
  scoreId: DocumentId;
  name: string;
  /** Feel name → the groove snapshot it resolves to. */
  feels: Record<string, GrooveSnapshot>;
  /**
   * Overrides for one specific cue, keyed by change id — the "this cue only"
   * escape hatch. Default editing scope is all cues sharing a Feel.
   */
  cueOverrides?: Record<string, GrooveSnapshot>;
  /** The resolver input: base state + measure-anchored changes. */
  timeline?: Arrangement;
  /** Versioned voicing-engine selection and every reproducibility input. */
  voiceLeading?: VoiceLeadingArrangementSettings;
}

/**
 * A saved tune: the notation itself. Browser-local today (the user's saved
 * charts); the same shape is what a repository catalogue score will use, so
 * both read through one validator.
 */
export interface ScoreDoc extends DocumentMeta {
  kind: 'score';
  name: string;
  score: Score;
}

/**
 * A distributable audio asset — a drum loop — as an IMMUTABLE revision (#175).
 *
 * The loop key used to be the whole identity: a bare string naming a file.
 * That cannot answer the questions publication actually needs — are these the
 * bytes we checked, may we redistribute them, where is the loop's downbeat,
 * how far can it be rate-matched before it sounds wrong. A groove that
 * references a media document gets all of that, and replacing a loop later
 * creates a NEW document rather than silently changing what every existing
 * arrangement plays.
 */
export interface MediaDoc extends DocumentMeta {
  kind: 'media';
  name: string;
  /** Path under public/, e.g. `drums/<key>.m4a`. */
  file: string;
  mimeType: string;
  /** Lowercase hex SHA-256 of the file's bytes. */
  checksum: string;
  /**
   * The loop's musical length in seconds — `beats × 60 / bpm`.
   *
   * NOT the container duration: every bundled asset's m4a runs ~50-60ms
   * LONGER, because AAC is not gapless and the encoder pads the tail. That
   * padding is exactly why nothing schedules against this field — the player
   * computes the period from `beats`/`bpm` and loops on that, so the sliver
   * of silence never plays. Kept because it is the human-readable length and
   * because it must never exceed the real audio (pinned by test).
   */
  seconds: number;
  /** Native tempo the loop was played at. */
  bpm?: number;
  /** Exact musical length in beats — the authored period. */
  beats?: number;
  beatsPerBar?: number;
  /**
   * What kind of asset the file is. 'loop' (the default when absent — every
   * existing drum document predates the field) is a tempo-locked repeating
   * period and REQUIRES bpm/beats/beatsPerBar; 'note' is a one-shot
   * instrument sample (a sampler's recorded pitch) where those fields mean
   * nothing and `midi` says which pitch was recorded.
   */
  role?: 'loop' | 'note';
  /** For role 'note': the MIDI pitch the sample sounds at rate 1. */
  midi?: number;
  /**
   * Absent means the loop's tempo divides its file into whole beats, so the
   * period above is exact and it sits on the grid. `false` means no known
   * tempo did — the loop still plays, but it drifts against the beat, and
   * anything presenting it should say so.
   */
  tempoVerified?: boolean;
  /**
   * Where the loop's "one" sits, in beats from the file start. Almost always
   * 0; a trimmed asset can carry a non-zero phase, and a scheduler that
   * ignores it puts the kit's downbeat off the tune's bar.
   */
  downbeatOffsetBeats?: number;
  /** Rate range the asset still sounds acceptable over (varispeed). */
  safeRateRange?: { min: number; max: number };
  /** Free-text grouping for the picker ("Swing", "Latin"). */
  category?: string;
}

export type StoredDocument = GroovePresetDoc | TuneArrangementDoc | ScoreDoc | MediaDoc;

// --- identity ---------------------------------------------------------------

/**
 * A stable id from a human-readable slug plus a short discriminator. The slug
 * makes repository diffs readable; the suffix keeps two same-titled tunes
 * apart. Ids are assigned ONCE and then never derived from the title again —
 * renaming a tune must not move its data.
 */
export function makeDocumentId(kind: StoredDocument['kind'], name: string, seed: string): string {
  const slug =
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48) || kind;
  return `${slug}-${shortHash(seed)}`;
}

/** Deterministic short hash (FNV-1a) — same input, same id, on every machine. */
export function shortHash(input: string): string {
  return fnv1a(input, 0x811c9dc5).toString(36).padStart(7, '0').slice(0, 7);
}

/**
 * A content-addressed identifier: two FNV-1a lanes with different offset
 * bases, so ~64 bits rather than 32.
 *
 * A store may use this as a revision id, and there a collision is not a
 * cosmetic clash — it is two different documents looking identical to the
 * conflict check, which turns a "your save is stale" into a silent clobber.
 * One lane is cheap enough to be tempting and too narrow to trust.
 */
export function contentHash(input: string): string {
  const a = fnv1a(input, 0x811c9dc5).toString(36).padStart(7, '0');
  const b = fnv1a(input, 0x01000193).toString(36).padStart(7, '0');
  return `${a}${b}`;
}

function fnv1a(input: string, basis: number): number {
  let h = basis;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/**
 * A document as it is STORED: without the revision id, which a store stamps
 * on read and must never accept from a client. Content-addressed stores hash
 * this form, or the hash would depend on itself.
 */
export function withoutRevision<T extends StoredDocument>(doc: T): T {
  if (doc.revisionId === undefined) return doc;
  const { revisionId: _ignored, ...rest } = doc;
  return rest as T;
}

/**
 * Ids address files on disk, so anything that could escape the allow-listed
 * directory is rejected at the domain boundary — before a path is ever built.
 */
const ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,79}$/;

export function isValidDocumentId(id: string): boolean {
  if (!ID_PATTERN.test(id)) return false;
  // Belt and braces: no traversal even if the pattern is later loosened.
  return !id.includes('..') && !id.includes('/') && !id.includes('\\');
}

// --- deterministic serialization -------------------------------------------

/**
 * Stable JSON: keys sorted at every depth, two-space indent, trailing
 * newline. Musical edits must produce reviewable diffs — a key-order change
 * that rewrites the whole file is worse than no diff at all.
 */
export function serializeDocument(doc: StoredDocument): string {
  return `${JSON.stringify(sortKeys(doc), null, 2)}\n`;
}

function sortKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (value && typeof value === 'object') {
    const src = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(src).sort()) out[key] = sortKeys(src[key]);
    return out;
  }
  return value;
}

// --- shape validation -------------------------------------------------------

export interface ValidationIssue {
  path: string;
  message: string;
}

/**
 * Is this a well-formed document of its declared kind? Identity, schema
 * version and the fields the kind must carry — but NOT whether the music
 * inside it is readable (see `validateDocument` in documents.ts).
 */
export function validateDocumentSchema(doc: unknown): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const fail = (path: string, message: string) => issues.push({ path, message });

  if (!doc || typeof doc !== 'object') {
    return [{ path: '', message: 'document must be an object' }];
  }
  const d = doc as Partial<StoredDocument> & Record<string, unknown>;

  if (typeof d.id !== 'string' || !isValidDocumentId(d.id)) {
    fail('id', 'missing or malformed id');
  }
  if (typeof d.schemaVersion !== 'number' || !Number.isInteger(d.schemaVersion)) {
    fail('schemaVersion', 'missing schema version');
  } else if (d.schemaVersion > SCHEMA_VERSION) {
    fail('schemaVersion', `written by a newer version (${d.schemaVersion} > ${SCHEMA_VERSION})`);
  }

  // Provenance and rights are optional, but a MALFORMED one is worse than an
  // absent one: it reads as an assessment that was never made.
  if (d.provenance !== undefined) {
    const kind = (d.provenance as Partial<DocumentProvenance>)?.kind;
    if (!kind || !(PROVENANCE_KINDS as readonly string[]).includes(kind)) {
      fail('provenance', `unknown provenance kind: ${String(kind)}`);
    }
  }
  if (d.rights !== undefined) {
    const status = (d.rights as Partial<DocumentRights>)?.status;
    if (!status || !(RIGHTS_STATUSES as readonly string[]).includes(status)) {
      fail('rights', `unknown rights status: ${String(status)}`);
    }
  }

  if (d.kind === 'score') {
    if (typeof d.name !== 'string' || !d.name.trim()) fail('name', 'score needs a name');
    if (!d.score || typeof d.score !== 'object') fail('score', 'score document needs a score');
  } else if (d.kind === 'groove') {
    if (typeof d.name !== 'string' || !d.name.trim()) fail('name', 'groove needs a name');
    if (!d.state || typeof d.state !== 'object') fail('state', 'groove needs a state');
  } else if (d.kind === 'arrangement') {
    if (typeof d.scoreId !== 'string' || !isValidDocumentId(d.scoreId)) {
      fail('scoreId', 'missing or malformed scoreId');
    }
    if (!d.feels || typeof d.feels !== 'object') {
      fail('feels', 'arrangement needs a feels map');
    }
    if (d.voiceLeading !== undefined) {
      const voiceLeading = d.voiceLeading as Partial<VoiceLeadingArrangementSettings>;
      if (!voiceLeading || typeof voiceLeading !== 'object') {
        fail('voiceLeading', 'voice-leading settings must be an object');
      } else {
        if (typeof voiceLeading.engineId !== 'string' || !voiceLeading.engineId.trim()) {
          fail('voiceLeading.engineId', 'voice-leading settings need an engine id');
        }
        const options = voiceLeading.options;
        if (!options || typeof options !== 'object') {
          fail('voiceLeading.options', 'voice-leading settings need reproducible options');
        } else {
          if (!['drop-2', 'drop-3', 'drop-2-3', 'joe-pass'].includes(options.voicingFamily)) {
            fail('voiceLeading.options.voicingFamily', 'unknown voicing family');
          }
          if (!['middle', 'upper'].includes(options.stringSet)) {
            fail('voiceLeading.options.stringSet', 'unknown string set');
          }
          for (const key of ['freeStringSet', 'avoidB9'] as const) {
            if (typeof options[key] !== 'boolean') {
              fail(`voiceLeading.options.${key}`, `${key} must be boolean`);
            }
          }
          if (!Number.isInteger(options.startingInversion)) {
            fail('voiceLeading.options.startingInversion', 'starting inversion must be an integer');
          }
        }
      }
    }
  } else if (d.kind === 'media') {
    if (typeof d.name !== 'string' || !d.name.trim()) fail('name', 'media needs a name');
    if (typeof d.file !== 'string' || !d.file.trim()) fail('file', 'media needs a file');
    if (typeof d.checksum !== 'string' || !/^[0-9a-f]{64}$/.test(d.checksum)) {
      fail('checksum', 'media needs a lowercase hex SHA-256 checksum');
    }
    const role = d.role ?? 'loop';
    if (role !== 'loop' && role !== 'note') fail('role', 'media role must be loop or note');
    // A loop is a musical PERIOD, so its tempo maths are required; a one-shot
    // note has no period and instead must say which pitch it sounds.
    const required =
      role === 'note' ? (['seconds'] as const) : (['seconds', 'bpm', 'beats', 'beatsPerBar'] as const);
    for (const key of required) {
      const value = d[key];
      if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
        fail(key, `media needs a positive ${key}`);
      }
    }
    if (role === 'note' && (typeof d.midi !== 'number' || !Number.isInteger(d.midi))) {
      fail('midi', 'a note sample needs the integer MIDI pitch it was recorded at');
    }
  } else {
    fail('kind', `unknown document kind: ${String(d.kind)}`);
  }
  return issues;
}

/**
 * Why this document may NOT be published or redistributed.
 *
 * FAILS CLOSED (#175): absent rights, `review-required` rights, or missing
 * provenance all block. Publication is the one place where "we never got
 * round to assessing it" and "it is cleared" must not look the same, so the
 * default answer is no and clearing something is a deliberate act.
 */
export function publicationIssues(doc: StoredDocument): string[] {
  const blockers: string[] = [];
  if (!doc.rights) blockers.push('no rights assessment');
  else if (doc.rights.status === 'review-required') blockers.push('rights need review');
  if (!doc.provenance) blockers.push('no provenance recorded');
  if (doc.kind === 'media' && !doc.checksum) blockers.push('no checksum to verify the bytes');
  return blockers;
}

/** May this document be published? False whenever anything is unclear. */
export function canPublish(doc: StoredDocument): boolean {
  return publicationIssues(doc).length === 0;
}
